import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MapPin, Check } from 'lucide-react';
import { GlassButton } from './GlassButton';
import { placesAutocomplete, placeDetails, AddressStructured } from '../../services/api';
import './AddressAutocompleteInput.css';

/**
 * AddressAutocompleteInput — Places-backed address field (specs/completed/
 * facebook-page-branding-live-push.md, Phase 5A). Replaces Project Settings'
 * free-text address <textarea>. Lives in /components/ui/ because it is a
 * generic form control with no Facebook or Project-Settings coupling — the
 * Onboarding wizard's company_address is the obvious second consumer (spec
 * Open Question 19, deliberately not done here).
 *
 * Fully controlled, owns no persistence — it never calls updateProject.
 * Project Settings PUTs client_info wholesale, so a component issuing its
 * own save would clobber every other field on the page
 * (ProjectSettings.tsx:108-115 documents that exact bug class). The only
 * job here is to hand the parent both halves of the value in one onChange.
 *
 * Not built on GlassInput: GlassInput has no onKeyDown/aria pass-through,
 * and full keyboard nav (arrow keys / Enter / Escape) plus
 * role="combobox"/aria-activedescendant require direct control of the
 * <input> element. Styled to match GlassInput visually via
 * AddressAutocompleteInput.css (CSS variables only, no hex literals).
 */

interface Suggestion {
  place_id: string;
  primary_text: string;
  secondary_text: string;
}

export interface AddressAutocompleteInputProps {
  value: string;
  structured: AddressStructured | null;
  onChange: (freeText: string, structured: AddressStructured | null) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  countryCode?: string;
}

const DEBOUNCE_MS = 350;
const MIN_CHARS = 4;
const BLUR_GRACE_MS = 150;

function makeManualEntry(text: string): AddressStructured {
  return {
    formatted_address: text,
    street_address: null,
    city: null,
    state: null,
    zip: null,
    country: null,
    country_code: null,
    latitude: null,
    longitude: null,
    place_id: null,
    source: 'manual_entry',
    confirmed_at: null,
    updated_at: new Date().toISOString(),
  };
}

function mintSessionToken(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for older browsers without crypto.randomUUID — collision risk
  // is irrelevant here (a session token just needs to be unique per field
  // focus, not cryptographically secure).
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const AddressAutocompleteInput: React.FC<AddressAutocompleteInputProps> = ({
  value,
  structured,
  onChange,
  label = 'Address',
  placeholder = 'e.g. Level 5, 123 Collins St, Melbourne VIC 3000',
  disabled = false,
  countryCode,
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [degraded, setDegraded] = useState(false);
  const [loading, setLoading] = useState(false);
  // F4 (specs/completed/fb-branding-address-hours-fix.md): a suburb-only
  // pick (no street line) is silently unpushable to Facebook later, in the
  // branding modal, with no warning here. Server-computed (never re-derived
  // in TS) from address_structuring.is_pushable via GET /places/details'
  // facebook_pushable/facebook_block_reason. Cleared on the next pick or edit.
  const [fbSuburbHint, setFbSuburbHint] = useState(false);

  const sessionTokenRef = useRef<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const inputId = useRef(`address-autocomplete-${Math.random().toString(36).slice(2, 9)}`);

  // Keep the visible text in sync when the parent's `value` changes from
  // outside (e.g. loading a different project) — but never rewrite an
  // untouched field's stored newlines on mere mount; this only fires on a
  // genuine prop change.
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setActiveIndex(-1);
    setSuggestions([]);
  }, []);

  const fetchSuggestions = useCallback(
    (text: string) => {
      if (!sessionTokenRef.current) {
        sessionTokenRef.current = mintSessionToken();
      }
      if (abortRef.current) {
        abortRef.current.abort();
      }
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);

      placesAutocomplete(text, sessionTokenRef.current, countryCode, controller.signal)
        .then((res) => {
          if (controller.signal.aborted) return;
          setLoading(false);
          if (res.degraded) {
            setDegraded(true);
            setSuggestions([]);
            setIsOpen(false);
            return;
          }
          setDegraded(false);
          setSuggestions(res.suggestions || []);
          setIsOpen((res.suggestions || []).length > 0);
          setActiveIndex(-1);
        })
        .catch(() => {
          if (controller.signal.aborted) return;
          setLoading(false);
          // Network-level failure the client itself hit (not a degrade
          // envelope from the backend) — treat the same as degraded so the
          // field never blocks Save Changes.
          setDegraded(true);
          setSuggestions([]);
          setIsOpen(false);
        });
    },
    [countryCode],
  );

  const handleInputChange = (text: string) => {
    setInputValue(text);
    setFbSuburbHint(false);
    // Typing without picking a suggestion is the manual-entry path — kept
    // live so Save Changes always has a consistent object, even if the user
    // never opens the dropdown at all.
    onChange(text, makeManualEntry(text));

    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = text.trim();
    if (trimmed.length < MIN_CHARS) {
      closeDropdown();
      if (abortRef.current) abortRef.current.abort();
      return;
    }

    debounceRef.current = setTimeout(() => fetchSuggestions(trimmed), DEBOUNCE_MS);
  };

  const handleFocus = () => {
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = mintSessionToken();
    }
  };

  const pickSuggestion = (suggestion: Suggestion) => {
    const token = sessionTokenRef.current || mintSessionToken();
    setLoading(true);
    placeDetails(suggestion.place_id, token)
      .then((res) => {
        setLoading(false);
        closeDropdown();
        // Session is spent the moment the terminating details call fires —
        // discard it regardless of outcome so the next focus mints a fresh
        // one (spec: "Session tokens are mandatory, for billing").
        sessionTokenRef.current = null;

        if (res.degraded || !res.address) {
          setDegraded(true);
          setFbSuburbHint(false);
          // Fall back to manual entry using whatever text is on screen —
          // the pick failed, but the field must keep working.
          onChange(inputValue, makeManualEntry(inputValue));
          return;
        }
        const formatted = res.address.formatted_address || inputValue;
        setInputValue(formatted);
        setFbSuburbHint(res.facebook_pushable === false && res.facebook_block_reason === 'skipped_incomplete_address');
        onChange(formatted, res.address);
      })
      .catch(() => {
        setLoading(false);
        closeDropdown();
        sessionTokenRef.current = null;
        setDegraded(true);
        setFbSuburbHint(false);
        onChange(inputValue, makeManualEntry(inputValue));
      });
  };

  const handleBlur = () => {
    // 150ms grace so a mousedown on a suggestion still registers before the
    // dropdown closes.
    blurTimeoutRef.current = setTimeout(() => {
      closeDropdown();
    }, BLUR_GRACE_MS);
  };

  const handleListMouseDown = (e: React.MouseEvent) => {
    // Prevent the input's blur from firing before the click handler runs.
    e.preventDefault();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || suggestions.length === 0) {
      if (e.key === 'Escape') closeDropdown();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < suggestions.length) {
        e.preventDefault();
        pickSuggestion(suggestions[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeDropdown();
    }
  };

  const handleConfirm = () => {
    if (!structured) return;
    onChange(value, { ...structured, confirmed_at: new Date().toISOString() });
  };

  const showBackfillConfirm = structured?.source === 'backfill_places' && !structured?.confirmed_at;
  const showNeedsReview = structured?.source === 'needs_manual_review';

  return (
    <div className="ps-form-group">
      {label && (
        <label className="ps-label" htmlFor={inputId.current}>
          {label}
        </label>
      )}

      {showBackfillConfirm && (
        <div className="address-autocomplete-confirm">
          <p>
            AIME worked this address out from what you'd typed. Check it's right and confirm, and
            we'll be able to use it on your Facebook Page.
          </p>
          <div className="address-autocomplete-breakdown">
            <span>{structured?.street_address}</span>
            <span>{structured?.city}</span>
            <span>{structured?.state}</span>
            <span>{structured?.zip}</span>
            <span>{structured?.country}</span>
          </div>
          <GlassButton variant="secondary" onClick={handleConfirm}>
            <Check size={16} /> Confirm address
          </GlassButton>
        </div>
      )}

      {showNeedsReview && (
        <div className="address-autocomplete-confirm">
          <p>AIME couldn't match this address — start typing and pick it from the suggestions.</p>
        </div>
      )}

      <div className="address-autocomplete">
        <MapPin size={16} className="address-autocomplete-icon" />
        <input
          id={inputId.current}
          type="text"
          className="address-autocomplete-input"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={`${inputId.current}-list`}
          aria-activedescendant={
            activeIndex >= 0 ? `${inputId.current}-option-${activeIndex}` : undefined
          }
          autoComplete="off"
          aria-busy={loading}
          placeholder={placeholder}
          value={inputValue}
          disabled={disabled}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onChange={(e) => handleInputChange(e.target.value)}
        />

        {isOpen && suggestions.length > 0 && (
          <ul
            id={`${inputId.current}-list`}
            role="listbox"
            className="address-autocomplete-list"
            ref={listRef}
            onMouseDown={handleListMouseDown}
          >
            {suggestions.map((s, idx) => (
              <li
                key={s.place_id}
                id={`${inputId.current}-option-${idx}`}
                role="option"
                aria-selected={idx === activeIndex}
                className={`address-autocomplete-option${idx === activeIndex ? ' is-active' : ''}`}
                onMouseEnter={() => setActiveIndex(idx)}
                onClick={() => pickSuggestion(s)}
              >
                <div className="address-autocomplete-option-primary">{s.primary_text}</div>
                {s.secondary_text && (
                  <div className="address-autocomplete-option-secondary">{s.secondary_text}</div>
                )}
              </li>
            ))}
            <li className="address-autocomplete-attribution" aria-hidden="true">
              Powered by Google
            </li>
          </ul>
        )}
      </div>

      {degraded ? (
        <p className="address-autocomplete-hint">
          Address suggestions are unavailable right now — you can type your address and save as
          usual.
        </p>
      ) : fbSuburbHint ? (
        <p className="address-autocomplete-hint">
          This is a suburb or area, not a street address. It will appear on your landing pages,
          but it can't be put on your Facebook Page. Pick a suggestion that includes a street
          number if you want it there.
        </p>
      ) : (
        <p className="address-autocomplete-hint">
          Pick your address from the suggestions to use it on your Facebook Page and in structured
          listings. Typed addresses still appear on your landing pages and website.
        </p>
      )}
    </div>
  );
};

export default AddressAutocompleteInput;
